// ============ HOOK PARA ACCEDER A DATOS OFFLINE ============
// client/src/lib/hooks/use-offline-data.ts

import { useState, useEffect, useCallback } from "react";
import {
  indexedDBService,
  OfflineInventory,
  OfflineProduct,
  PendingOperation,
} from "@/lib/db/indexed-db";
import { toast } from "@/hooks/use-toast";
import { useOffline } from "./use-offline";
import { useOfflineContext } from "@/lib/contexts/offline-context";
import { AuthService } from "@/lib/services/auth-offline"; // NUEVO: Para validar sesión offline
// COMENTARIO: Estos imports aseguran consistencia con la arquitectura offline actualizada

interface UseOfflineDataProps {
  shopId: string;
  fetchOnlineData?: () => Promise<any>;
  isOffline?: boolean; // ← AGREGAR ESTA LÍNEA
}

export function useOfflineInventory({
  shopId,
  fetchOnlineData,
  isOffline: propsIsOffline, // ← AGREGAR ESTE PARÁMETRO
}: UseOfflineDataProps) {
  const [data, setData] = useState<OfflineInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOffline: contextIsOffline } = useOffline();
  const { incrementPending } = useOfflineContext();

  // ============ USAR EL isOffline DEL PROP SI SE PROVEE, SINO EL DEL CONTEXTO ============
  const isOffline =
    propsIsOffline !== undefined ? propsIsOffline : contextIsOffline;

  // ============ FUNCIÓN PARA CARGAR DATOS ============
  const loadData = useCallback(async () => {
    if (!shopId) {
      // Si no hay shopId por props, intentar obtenerlo de la sesión activa
      const session = await AuthService.getSession();
      if (!session?.shopId) {
        console.warn("⚠️ No hay shopId disponible para cargar inventario");
        setLoading(false);
        return;
      }
    }

    const effectiveShopId = shopId || (await AuthService.getSession())?.shopId;
    if (!effectiveShopId) return;

    try {
      setLoading(true);

      if (isOffline) {
        // 📴 Modo offline: cargar exclusivamente desde IndexedDB
        console.log("📴 Modo offline: cargando inventario desde IndexedDB");
        const offlineData =
          await indexedDBService.getInventory(effectiveShopId);
        console.log(`📦 Datos offline cargados: ${offlineData.length} items`);
        setData(offlineData);
      } else {
        // 🌐 Modo online: intentar cargar desde API con fallback a cache offline
        console.log("🌐 Modo online: intentando cargar desde API");

        try {
          if (fetchOnlineData) {
            const onlineData = await fetchOnlineData();
            console.log(`📦 Datos online cargados: ${onlineData.length} items`);
            setData(onlineData);

            // Guardar en IndexedDB para uso offline futuro (backup)
            await indexedDBService.saveInventory(onlineData);
            console.log("💾 Datos guardados en IndexedDB para offline");
          }
        } catch (apiError) {
          // Fallback: si la API falla pero hay datos offline, usarlos
          console.warn(
            "⚠️ API falló, usando datos offline como fallback",
            apiError,
          );
          const offlineData =
            await indexedDBService.getInventory(effectiveShopId);
          if (offlineData.length > 0) {
            setData(offlineData);
            toast({
              title: "Modo offline activado",
              description:
                "Usando datos cacheados. Los cambios se sincronizarán después.",
              variant: "default",
            });
          }
        }
      }
    } catch (err) {
      console.error("❌ Error cargando inventario:", err);
      setError("No se pudieron cargar los datos del inventario");
    } finally {
      setLoading(false);
    }
  }, [shopId, isOffline, fetchOnlineData]);
  // COMENTARIO:
  // - AuthService.getSession() provee shopId seguro si no viene por props.
  // - El try/catch anidado permite fallback a offline si la API falla temporalmente.
  // - El toast informa al usuario cuando se activa el modo offline automáticamente.

  // Cargar datos al montar y cuando cambien dependencias
  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============ ESCUCHAR CAMBIOS EN INDEXEDDB ============
  useEffect(() => {
    // Función para manejar cambios en los datos
    const handleInventoryChange = () => {
      console.log("🔄 Detectado cambio en inventario, recargando...");
      loadData();
    };

    // Escuchar evento personalizado
    window.addEventListener("inventory-changed", handleInventoryChange);

    // También escuchar storage events (por si otra pestaña cambia los datos)
    window.addEventListener("storage", handleInventoryChange);

    return () => {
      window.removeEventListener("inventory-changed", handleInventoryChange);
      window.removeEventListener("storage", handleInventoryChange);
    };
  }, [loadData]);

  // Función para guardar cambios offline con cola priorizada
  const saveOffline = async (
    item: any,
    operationType: "create" | "update" | "delete" = "update",
  ) => {
    try {
      // 1. Obtener sesión actual para extraer shopId y validar autenticación
      const session = await AuthService.getSession();
      if (!session?.shopId) {
        throw new Error("No hay sesión activa para guardar operación offline");
      }

      const offlineItem: OfflineInventory = {
        ...item,
        lastUpdated: new Date().toISOString(),
      };

      // 2. Guardar en IndexedDB (inventario local)
      await indexedDBService.saveInventoryItem(offlineItem);

      // 3. Encolar operación para sincronización futura con estructura PendingOperation
      const pendingOp: PendingOperation = {
        id: crypto.randomUUID(),
        type: operationType,
        entityType: "inventory",
        data: offlineItem,
        timestamp: new Date().toISOString(),
        retryCount: 0,
        shopId: session.shopId,
        priority: "normal", // inventory tiene prioridad normal (ventas son "high")
        maxRetries: 3,
        originalCreatedAt: new Date().toISOString(), // CRÍTICO: timestamp real de creación offline
        status: "pending",
      };

      await indexedDBService.addPendingOperation(pendingOp);
      incrementPending("inventory");

      // 4. Disparar evento para actualizar UI
      window.dispatchEvent(new CustomEvent("inventory-changed"));

      // 5. Actualizar estado local inmediatamente para UI más rápida
      setData((prev) => {
        const index = prev.findIndex((i) => i.id === item.id);
        if (index >= 0) {
          const newData = [...prev];
          newData[index] = offlineItem;
          return newData;
        } else {
          return [...prev, offlineItem];
        }
      });

      console.log(
        `✅ Operación offline encolada: ${operationType} inventory item ${item.id}`,
      );
      return { success: true, operationId: pendingOp.id };
    } catch (err) {
      console.error("Error guardando offline:", err);
      return { success: false, error: err };
    }
  };
  // COMENTARIO:
  // - AuthService.getSession() provee shopId seguro sin depender de props.
  // - PendingOperation estructura la cola con prioridad, reintentos y timestamp original.
  // - originalCreatedAt es CRÍTICO: el backend lo usa para resolver conflictos de inventario.

  return {
    data,
    loading,
    error,
    saveOffline,
    isOffline,
    refetch: loadData, // Exponer función para recargar manualmente
  };
}

export function useOfflineProducts({
  fetchOnlineData,
  isOffline: propsIsOffline, // ← AGREGAR ESTE PARÁMETRO
}: {
  fetchOnlineData?: () => Promise<any>;
  isOffline?: boolean; // ← AGREGAR ESTA LÍNEA
}) {
  const [data, setData] = useState<OfflineProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOffline: contextIsOffline } = useOffline();

  // ============ USAR EL isOffline DEL PROP SI SE PROVEE, SINO EL DEL CONTEXTO ============
  const isOffline =
    propsIsOffline !== undefined ? propsIsOffline : contextIsOffline;

  // ============ FUNCIÓN PARA CARGAR PRODUCTOS ============
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      if (isOffline) {
        // 📴 Modo offline: cargar exclusivamente desde IndexedDB
        console.log("📴 Modo offline: cargando productos desde IndexedDB");
        const offlineData = await indexedDBService.getProducts();
        console.log(
          `📦 Productos offline cargados: ${offlineData.length} items`,
        );
        setData(offlineData);
      } else {
        // 🌐 Modo online: intentar cargar desde API con fallback a cache offline
        console.log("🌐 Modo online: intentando cargar productos desde API");

        try {
          if (fetchOnlineData) {
            const onlineData = await fetchOnlineData();
            console.log(
              `📦 Productos online cargados: ${onlineData.length} items`,
            );
            setData(onlineData);

            // Guardar en IndexedDB para uso offline futuro (catálogo completo)
            await indexedDBService.saveProducts(onlineData);
            console.log("💾 Productos guardados en IndexedDB para offline");
          }
        } catch (apiError) {
          // Fallback: si la API falla pero hay catálogo offline, usarlo
          console.warn(
            "⚠️ API de productos falló, usando catálogo offline como fallback",
            apiError,
          );
          const offlineData = await indexedDBService.getProducts();
          if (offlineData.length > 0) {
            setData(offlineData);
          }
        }
      }
    } catch (err) {
      console.error("❌ Error cargando productos:", err);
      setError("No se pudieron cargar los productos");
    } finally {
      setLoading(false);
    }
  }, [isOffline, fetchOnlineData]);
  // COMENTARIO:
  // - Mantiene la misma estructura resiliente que useOfflineInventory.
  // - El fallback protege al cajero de quedarse sin catálogo si la API falla.
  // - Se omite toast aquí para evitar imports adicionales; usa console.warn en su lugar.

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============ ESCUCHAR CAMBIOS EN INDEXEDDB ============
  useEffect(() => {
    const handleProductsChange = () => {
      loadData();
    };

    window.addEventListener("products-changed", handleProductsChange);
    window.addEventListener("storage", handleProductsChange);

    return () => {
      window.removeEventListener("products-changed", handleProductsChange);
      window.removeEventListener("storage", handleProductsChange);
    };
  }, [loadData]);

  return {
    data,
    loading,
    error,
    isOffline,
    refetch: loadData,
  };
}
